import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useConfirm } from '../../hooks/useConfirm.jsx';
import { usePreferences } from '../../hooks/usePreferences.jsx';
import { SettingsModal } from '../Settings/SettingsModal';
import { ErrorRetryCard } from '../common/ErrorRetryCard.jsx';
import { OfflineNotice } from '../common/OfflineNotice.jsx';
import { MainNav } from '../layout/MainNav.jsx';
import { AccountMenu } from '../layout/AccountMenu.jsx';
import { MobileMoreSheet } from '../layout/MobileMoreSheet.jsx';
import { MOBILE_PRIMARY_ITEMS } from '../layout/navItems.js';
import SinodoDisclaimerModal from './SinodoDisclaimerModal.jsx';
import { fetchPlanCatalog } from '../../services/planCatalog.js';
import { apiClient, LONG_REQUEST_TIMEOUT_MS } from '../../services/http.js';
import { openChatStream, readWithSilenceTimeout } from '../../services/chatStream.js';
import { createSmoothWriter } from '../../services/smoothStream.js';
import { emitCreditsUpdated } from '../../services/creditsEvents.js';
import { useModalA11y } from '../../hooks/useModalA11y.js';

const SINODO_DISCLAIMER_KEY = 'sinodo_disclaimer_acknowledged_v1';



// P23F: painel de histórico/navegação da sidebar do Chat vira drawer
// sobreposto (com backdrop) em telas intermediárias, não só no mobile —
// "desktop intermediário" = 768-1023px (entre os breakpoints md e lg do
// Tailwind). A partir de lg (1024px+) volta a ser um painel fixo, empurrando
// o conteúdo, como já era antes.
function getScreenTier() {
  if (typeof window === 'undefined') return 'wide';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'intermediate';
  return 'wide';
}

export const ChatContainer = () => {
  const { showToast, showApiErrorToast } = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [messages, setMessages] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState('padre_amigo');
  const [sinodoMode, setSinodoMode] = useState(false);
  const [showSinodoDisclaimer, setShowSinodoDisclaimer] = useState(false);
  const [synodCost, setSynodCost] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [planCatalog, setPlanCatalog] = useState([]);
  const { preferences } = usePreferences();

  const LANGUAGE_MAP = {
    'pt-BR': 'Português',
    'en-US': 'English',
    'es-ES': 'Español'
  };
  const [loading, setLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [screenTier, setScreenTier] = useState(getScreenTier);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => getScreenTier() === 'intermediate');
  const [conversations, setConversations] = useState([]);
  const [conversationsError, setConversationsError] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [showHistorySearch, setShowHistorySearch] = useState(false);
  const generateUUID = () => {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
    // Fallback para contextos não seguros (http) ou WebViews antigas
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const [conversationId, setConversationId] = useState(generateUUID);
  const abortControllerRef = useRef(null);
  // Por que o stream foi abortado: distingue o clique do usuário em "Parar
  // resposta" (mantém o texto parcial, sem retry) de uma queda detectada
  // (perda de rede/troca de interface, watchdog de silêncio) — nesses casos
  // oferecemos retry em vez de tratar como cancelamento voluntário.
  const abortReasonRef = useRef('user');

  useEffect(() => {
    const handleResize = () => setScreenTier(getScreenTier());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // P23F: no mobile, o painel é um overlay controlado pelo hambúrguer. No
  // "desktop intermediário" (768-1023px), expandir o painel (chevron) também
  // vira overlay com backdrop — só a partir de lg (1024px+) ele empurra o
  // conteúdo de forma persistente, como sempre foi.
  const isSidebarOverlay = isMobileMenuOpen || (screenTier === 'intermediate' && !isSidebarCollapsed);
  const isSidebarVisible = screenTier !== 'mobile' || isMobileMenuOpen;

  const closeSidebarOverlay = () => {
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    if (screenTier === 'intermediate' && !isSidebarCollapsed) setIsSidebarCollapsed(true);
  };

  // Acessibilidade do drawer (P23F): Esc fecha, foco fica preso dentro do
  // painel enquanto ele se comporta como overlay, e volta pro controle que
  // abriu o painel ao fechar.
  const modalA11y = useModalA11y(isSidebarOverlay, closeSidebarOverlay, 'sidebar-title');

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const fetchAvailableCredits = useCallback(async () => {
    try {
      const [creditsRes, catalog, configRes] = await Promise.all([
        apiClient.request('/billing/credits'),
        fetchPlanCatalog(),
        apiClient.request('/chat/config')
      ]);
      
      let currentPlanId = null;
      let returnedPlan = null;
      if (creditsRes.res.ok) {
        setAvailableCredits(creditsRes.data?.available ?? 0);
        emitCreditsUpdated({ available: creditsRes.data?.available ?? 0, plan: creditsRes.data?.plan ?? null });
        returnedPlan = creditsRes.data?.plan;
        currentPlanId = returnedPlan && returnedPlan !== 'trial' ? returnedPlan : null;
        setCurrentPlan(currentPlanId);
      } else {
        showApiErrorToast(creditsRes.res, creditsRes.data, 'Não foi possível atualizar seus créditos.', { onRetry: fetchAvailableCredits });
      }

      if (configRes.res.ok) {
        setPersonas(configRes.data?.personas || []);
      }

      if (catalog && catalog.length > 0) {
        setPlanCatalog(catalog);
        const activePlan = catalog.find(p => p.plan_id === returnedPlan) || catalog[catalog.length - 1];
        if (activePlan?.entitlements?.synod_credit_cost !== undefined) {
          setSynodCost(activePlan.entitlements.synod_credit_cost);
        }
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível atualizar seus créditos.', { onRetry: fetchAvailableCredits });
    }
  }, [showApiErrorToast]);

  const fetchConversations = useCallback(async () => {
    try {
      const { res, data } = await apiClient.request('/chat/conversations');
      if (res.ok && Array.isArray(data)) {
        setConversations(data);
        setConversationsError(null);
        return;
      }
      setConversationsError(apiClient.parseApiError(data, 'Não foi possível carregar seu histórico.'));
    } catch {
      setConversationsError('Falha de conexão ao carregar seu histórico.');
    }
  }, []);

  useEffect(() => { fetchAvailableCredits(); fetchConversations(); }, [fetchAvailableCredits, fetchConversations]);

  const loadConversation = async (id) => {
    abortControllerRef.current?.abort();
    setConversationId(id);
    setMessages([]);
    closeSidebarOverlay();
    try {
      const { res, data } = await apiClient.request(`/chat/${id}/messages`);
      if (res.ok && Array.isArray(data)) {
        setMessages(data.map((m, i) => ({
          id: m.id ?? `loaded_${i}`,
          role: m.role,
          content: m.content,
          citations: Array.isArray(m.citations) ? m.citations : [],
          timestamp: m.created_at,
        })));
        return;
      }
      showApiErrorToast(res, data, 'Não foi possível carregar esta conversa.', { onRetry: () => loadConversation(id) });
    } catch {
      showApiErrorToast(null, null, 'Não foi possível carregar esta conversa.', { onRetry: () => loadConversation(id) });
    }
  };

  const newConversation = () => {
    abortControllerRef.current?.abort();
    setConversationId(generateUUID());
    setMessages([]);
    closeSidebarOverlay();
    fetchConversations();
  };

  const startRename = (conv) => {
    setRenamingId(conv.id);
    setRenameValue(conv.title);
  };

  const submitRename = async (id) => {
    if (!renameValue.trim()) return;
    const title = renameValue.trim();
    try {
      const { res, data } = await apiClient.request(`/chat/conversations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (res.ok) {
        setConversations(cs => cs.map(c => c.id === id ? { ...c, title } : c));
      } else {
        showApiErrorToast(res, data, 'Não foi possível renomear a conversa.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível renomear a conversa.');
    }
    setRenamingId(null);
  };

  const deleteConversation = async (id) => {
    try {
      const { res, data } = await apiClient.request(`/chat/conversations/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        showApiErrorToast(res, data, 'Não foi possível excluir a conversa.', { onRetry: () => deleteConversation(id) });
        return;
      }
      setConversations(cs => cs.filter(c => c.id !== id));
      if (conversationId === id) newConversation();
    } catch {
      showApiErrorToast(null, null, 'Não foi possível excluir a conversa.', { onRetry: () => deleteConversation(id) });
    }
  };

  const handleToggleSinodo = () => {
    if (sinodoMode) {
      setSinodoMode(false);
      return;
    }
    if (localStorage.getItem(SINODO_DISCLAIMER_KEY)) {
      setSinodoMode(true);
      return;
    }
    setShowSinodoDisclaimer(true);
  };

  const handleAcceptSinodoDisclaimer = () => {
    localStorage.setItem(SINODO_DISCLAIMER_KEY, '1');
    setShowSinodoDisclaimer(false);
    setSinodoMode(true);
  };

  const handleSendSinodo = async (content) => {
    if (synodCost === null) {
      showApiErrorToast(null, null, 'Aguarde o carregamento das configurações de conta.');
      return;
    }
    if (availableCredits < synodCost) {
      if (await confirm({
        title: 'Créditos Insuficientes',
        message: `O Sínodo Virtual custa ${synodCost} crédito${synodCost > 1 ? 's' : ''} e você não tem créditos suficientes.\n\nDeseja acessar a página de planos?`,
        confirmText: 'Ver Planos'
      })) {
        navigate('/billing');
      }
      return;
    }
    const confirmed = await confirm({
      title: 'Confirmar Consulta ao Sínodo',
      message: `Esta consulta ao Sínodo Virtual custará ${synodCost} crédito${synodCost > 1 ? 's' : ''}.\n\n` +
      'É uma simulação gerada por IA (não um sínodo oficial, sacerdote real ou orientação pastoral individual), ' +
      'com respostas em estilo "Teólogo" e "Padre Amigo" seguidas de uma síntese.\n\n' +
      'Deseja continuar?',
      confirmText: 'Confirmar'
    });
    if (!confirmed) return;
    setLoading(true);
    try {
      const assistantId = globalThis.crypto?.randomUUID?.() ?? `m_${Date.now()}`;
      setMessages(prev => [
        ...prev,
        { id: `u_${Date.now()}`, role: 'user', content, timestamp: new Date().toISOString() },
        { id: assistantId, role: 'assistant', content: '', citations: [], timestamp: new Date().toISOString() },
      ]);

      const { res, data } = await apiClient.request(`/chat/${conversationId}/sinodo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content, language: 'Português' }),
        // Não é streaming: a resposta só volta depois de três completions do
        // LLM. O timeout padrão de 15s cortaria a chamada toda vez.
        timeoutMs: LONG_REQUEST_TIMEOUT_MS,
      });

      if (!res.ok) {
        setMessages(prev => prev.filter(m => m.id !== assistantId));
        showApiErrorToast(res, data, 'Falha ao consultar o Sínodo Virtual.', { onRetry: () => handleSendSinodo(content) });
        return;
      }

      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: data.formatted } : m));
      if (typeof data.remaining_credits === 'number') setAvailableCredits(data.remaining_credits);
      fetchConversations();
    } catch {
      showApiErrorToast(null, null, 'Erro ao consultar o Sínodo Virtual.', { onRetry: () => handleSendSinodo(content) });
    } finally {
      setLoading(false);
      fetchAvailableCredits();
    }
  };

  const handleSendMessage = async (content) => {
    if (sinodoMode) {
      await handleSendSinodo(content);
      return;
    }
    if (availableCredits <= 0) {
      if (await confirm({
        title: 'Créditos Esgotados',
        message: 'Seus créditos acabaram.\n\nDeseja acessar a página de planos para continuar?',
        confirmText: 'Ver Planos'
      })) {
        navigate('/billing');
      }
      return;
    }
    setLoading(true);
    const assistantId = globalThis.crypto?.randomUUID?.() ?? `m_${Date.now()}`;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let networkListenersCleanup = () => {};
    let writer = null;
    try {
      setMessages(prev => [
        ...prev,
        { id: `u_${Date.now()}`, role: 'user', content, timestamp: new Date().toISOString() },
        { id: assistantId, role: 'assistant', content: '', citations: [], timestamp: new Date().toISOString() },
      ]);

      abortReasonRef.current = null;

      // Uma queda de Wi-Fi -> 4G costuma matar o stream sem avisar o cliente
      // (sem FIN, sem erro) — em vez de deixar a bolha vazia parada, 'offline'
      // aborta com motivo 'network' e oferece retry.
      //
      // NÃO usamos App.addListener('appStateChange') para abortar ao voltar do
      // background: o usuário troca de app o tempo todo no celular (notificação,
      // copiar um texto) e abortar em toda volta mataria streams perfeitamente
      // saudáveis. O caso que aquilo tentava cobrir — conexão que morreu
      // enquanto o app estava suspenso — já é coberto pelo watchdog de silêncio,
      // que dispara assim que os timers voltam a rodar.
      {
        const onOffline = () => {
          abortReasonRef.current = 'network';
          controller.abort();
        };
        window.addEventListener('offline', onOffline);
        networkListenersCleanup = () => window.removeEventListener('offline', onOffline);
      }

      const res = await openChatStream(`/chat/${conversationId}`, {
        message: content,
        persona: selectedPersona,
        language: LANGUAGE_MAP[preferences.language] || 'Português',
        client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }, { signal: controller.signal });
      if (!res.ok || !res.body) {
        // Falhou antes de começar a transmitir (ex: 403 de persona não
        // liberada pelo plano) — o corpo é JSON normal, não SSE.
        const data = await res.json().catch(() => null);
        setMessages(prev => prev.filter(m => m.id !== assistantId));
        
        if (data?.kind === 'upgrade' || data?.kind === 'payment_required') {
          if (await confirm({
            title: 'Créditos Esgotados',
            message: 'Seus créditos acabaram.\n\nDeseja acessar a página de planos para continuar?',
            confirmText: 'Ver Planos'
          })) {
            navigate('/billing');
          }
        } else {
          showApiErrorToast(res, data, 'Não foi possível enviar a mensagem.', { onRetry: () => handleSendMessage(content) });
        }
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamError = null;
      let streamEnded = false;
      let assistantHasContent = false;

      // Revela o texto aos poucos (ver services/smoothStream.js): a resposta não pula inteira na tela.
      writer = createSmoothWriter((chunk) => {
        setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, content: `${m.content || ''}${chunk}` } : m));
      });

      const applyEvent = (evt) => {
        if (!evt || typeof evt !== 'object') return;
        if (evt.type === 'start' || evt.type === 'citations') {
          const citations = Array.isArray(evt.citations) ? evt.citations : [];
          setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, citations } : m));
        }
        if (evt.type === 'delta' && typeof evt.text === 'string') {
          assistantHasContent = true;
          writer.push(evt.text);
        }
        if (evt.type === 'end') {
          streamEnded = true;
          if (typeof evt.remaining_credits === 'number') {
            setAvailableCredits(evt.remaining_credits);
            emitCreditsUpdated({ available: evt.remaining_credits });
          }
          fetchConversations();
        }
        if (evt.type === 'error') {
          streamError = { message: evt.message || 'Erro do assistente', kind: evt.kind };
        }
      };

      const parseAndApply = (dataStr) => {
        try {
          applyEvent(JSON.parse(dataStr));
        } catch {
          console.warn('Bloco SSE ignorado por JSON inválido:', dataStr);
        }
      };

      while (true) {
        let value, done;
        try {
          ({ value, done } = await readWithSilenceTimeout(reader));
        } catch (readErr) {
          if (readErr?.message === 'STREAM_SILENCE_TIMEOUT') {
            abortReasonRef.current = 'timeout';
            controller.abort();
            await reader.cancel().catch(() => {});
          }
          throw readErr;
        }
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() || '';
        for (const block of blocks) {
          const dataStr = block.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trimStart()).join('\n').trim();
          if (dataStr) parseAndApply(dataStr);
        }
        if (streamError) break; // Aborta leitura se for erro de negócio
      }
      
      if (!streamError) {
        const tail = buffer.trim();
        if (tail) {
          const dataStr = tail.split('\n').filter(l => l.startsWith('data:')).map(l => l.slice(5).trimStart()).join('\n').trim();
          if (dataStr) parseAndApply(dataStr);
        }
      }

      // Termina de exibir o que já chegou (erro/abort exibem tudo de uma vez).
      if (streamError) writer.flush();
      else await writer.drain();

      if (streamError) {
        if (!assistantHasContent) {
          setMessages(prev => prev.filter(m => m.id !== assistantId));
        }
        const isUpgrade = streamError.kind === 'upgrade' || streamError.kind === 'payment_required';
        if (isUpgrade) {
          if (await confirm({
            title: 'Créditos Esgotados',
            message: 'Seus créditos acabaram.\n\nDeseja acessar a página de planos para continuar?',
            confirmText: 'Ver Planos'
          })) {
            navigate('/billing');
          }
        } else {
          showToast({
            type: 'error',
            message: streamError.message,
          });
        }
        return;
      }

      if (!streamEnded && !assistantHasContent) {
        throw new Error('network_drop_empty');
      }

    } catch (err) {
      writer?.flush(); // texto já recebido entra antes de qualquer aviso de interrupção
      const abortReason = abortReasonRef.current;
      const isAbortLike = err?.name === 'AbortError' || err?.message === 'STREAM_SILENCE_TIMEOUT';
      if (isAbortLike && (abortReason === 'network' || abortReason === 'timeout')) {
        // Queda de rede detectada (offline/appStateChange) ou watchdog de
        // silêncio (30s sem chunk, conexão morta sem FIN) — nos dois casos a
        // resposta não deve ficar pendurada: descarta a bolha vazia (mantém
        // o texto parcial se algo já chegou) e oferece retry.
        setMessages(prev => {
          const hasContent = prev.find(m => m.id === assistantId)?.content?.trim();
          return hasContent ? prev : prev.filter(m => m.id !== assistantId);
        });
        showToast({
          type: 'error',
          message: abortReason === 'timeout'
            ? 'A resposta demorou demais. Tentar novamente.'
            : 'Conexão perdida. Tentar novamente.',
          actionLabel: 'Tentar novamente',
          onAction: () => handleSendMessage(content),
        });
      } else if (isAbortLike) {
        // Cancelamento voluntário (botão "Parar resposta") ou desmontagem do
        // componente: mantém o texto parcial já renderizado, sem toast de
        // erro nem retry automático.
        setMessages(prev => prev.map(m => (
          m.id === assistantId ? { ...m, content: `${m.content || ''}\n\n_(Resposta interrompida)_` } : m
        )));
      } else {
        // Queda de rede
        setMessages(prev => {
          const hasContent = prev.find(m => m.id === assistantId)?.content?.trim();
          if (!hasContent) {
            return prev.filter(m => m.id !== assistantId);
          }
          return prev;
        });
        
        showToast({
          type: 'error',
          message: 'Erro ao conectar. Verifique sua rede.',
          actionLabel: 'Tentar novamente',
          onAction: () => handleSendMessage(content),
        });
      }
    } finally {
      writer?.flush();
      networkListenersCleanup();
      setLoading(false);
      abortControllerRef.current = null;
      fetchAvailableCredits();
    }
  };

  const handleEditLastMessage = async (newText) => {
    try {
      const { res, data } = await apiClient.deleteLastMessage({ conversationId });
      if (!res.ok) {
        showApiErrorToast(res, data, 'Não foi possível editar a mensagem.', { onRetry: () => handleEditLastMessage(newText) });
        return;
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível editar a mensagem.', { onRetry: () => handleEditLastMessage(newText) });
      return;
    }
    setMessages(prev => {
      const lastUserIdx = prev.reduce((last, m, i) => (m.role === 'user' ? i : last), -1);
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
    fetchAvailableCredits();
    handleSendMessage(newText);
  };

  const handleDeleteLastMessage = async () => {
    try {
      const { res, data } = await apiClient.deleteLastMessage({ conversationId });
      if (!res.ok) {
        showApiErrorToast(res, data, 'Não foi possível excluir a mensagem.', { onRetry: handleDeleteLastMessage });
        return;
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível excluir a mensagem.', { onRetry: handleDeleteLastMessage });
      return;
    }
    setMessages(prev => {
      const lastUserIdx = prev.reduce((last, m, i) => (m.role === 'user' ? i : last), -1);
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
    fetchAvailableCredits();
    fetchConversations();
  };

  const handleStopGeneration = () => {
    abortReasonRef.current = 'user';
    abortControllerRef.current?.abort();
  };

  const handleRegenerateLastMessage = async () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return;
    if (!(await confirm({
      title: 'Gerar Nova Resposta',
      message: 'Gerar uma nova resposta vai consumir 1 crédito. Deseja continuar?',
      confirmText: 'Gerar Resposta'
    }))) return;
    handleEditLastMessage(lastUserMsg.content);
  };

  const filteredConversations = historySearch.trim()
    ? conversations.filter(c => (c.title || 'Sem título').toLowerCase().includes(historySearch.trim().toLowerCase()))
    : conversations;

  const currentConversationTitle = conversations.find(c => c.id === conversationId)?.title || 'Nova conversa';

  return (
    <div className="font-body text-on-background h-dvh overflow-hidden flex flex-col md:flex-row antialiased bg-surface">
      <OfflineNotice />
      {/* Mobile Top Bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 flex justify-between items-center px-4 pt-safe-top h-mobile-header z-40 bg-surface-container-low shadow-[0px_4px_16px_rgba(5,7,10,0.5)] border-b border-outline-variant/10">
        <div className="flex items-center gap-3">
          <button
            className="text-on-surface hover:text-primary transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Abrir histórico de conversas"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="material-symbols-outlined text-2xl">menu</span>
          </button>
          <img src="/logo-64.webp" alt="" className="w-6 h-6 rounded-full" />
          <span className="font-serif italic text-lg text-primary tracking-tighter">CatholicGPT</span>
        </div>
        <button className="text-on-surface hover:text-primary transition-colors" onClick={newConversation}>
          <span className="material-symbols-outlined text-2xl">add</span>
        </button>
      </header>

      {/* Sidebar — vira drawer (overlay + backdrop) no mobile e no desktop
          intermediário (768-1023px); painel fixo, empurrando o conteúdo, só
          a partir de lg (P23F). */}
      <aside
        {...(isSidebarOverlay ? modalA11y : {})}
        aria-label="Navegação e histórico de conversas"
        className={`
          ${isSidebarVisible ? 'flex' : 'hidden'} ${isSidebarOverlay ? 'absolute' : 'relative'} flex-col h-dvh
          ${isSidebarCollapsed ? 'w-sidebar-collapsed' : screenTier === 'wide' ? 'w-sidebar-expanded' : 'w-72'}
          bg-surface-container-low transition-all duration-300 z-50 flex-shrink-0
        `}
      >

        <div className={`flex flex-col h-full py-4 md:py-4 ${isSidebarCollapsed ? 'px-2' : 'px-3'} transition-all`}>

          {/* Brand + Collapse toggle */}
          <div className={`flex items-center mb-3 md:mb-3 ${isSidebarCollapsed ? 'justify-center' : 'justify-between px-1'}`}>
            {!isSidebarCollapsed && (
              <div className="flex items-center gap-2.5 cursor-pointer group">
                <div className="w-8 h-8 md:w-7 md:h-7 rounded-full bg-white flex items-center justify-center border border-outline-variant/20 overflow-hidden">
                  <img src="/logo-64.webp" alt="CatholicGPT" className="w-6 h-6 md:w-5 md:h-5 object-contain rounded-full p-0.5" />
                </div>
                <h1 className="font-serif italic text-lg md:text-base text-primary tracking-tighter leading-none">CatholicGPT</h1>
              </div>
            )}
            {isSidebarCollapsed && (
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-outline-variant/20 overflow-hidden">
                <img src="/logo-64.webp" alt="CatholicGPT" className="w-6 h-6 object-contain rounded-full p-0.5" />
              </div>
            )}
            <button
              className="flex min-h-control min-w-control md:min-h-control-sm md:min-w-control-sm items-center justify-center text-on-surface-variant hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
              onClick={() => {
                if (screenTier === 'mobile') {
                  closeSidebarOverlay();
                } else {
                  setIsSidebarCollapsed((c) => !c);
                }
              }}
              title={screenTier === 'mobile' ? 'Fechar' : (isSidebarCollapsed ? 'Expandir sidebar' : 'Recolher sidebar')}
            >
              <span className="material-symbols-outlined text-[18px] md:text-[16px]">
                {screenTier === 'mobile' ? 'close' : (isSidebarCollapsed ? 'chevron_right' : 'chevron_left')}
              </span>
            </button>
          </div>

          {/* Novo Chat — texto clicável em negrito, cor primária (P23K:
              antes um botão grande estilo pill; pedido explícito do usuário
              para reduzir densidade, no mesmo espírito do Claude). */}
          <button
            onClick={newConversation}
            title="Novo Chat"
            className={`flex items-center gap-2 mb-3 text-primary font-bold hover:opacity-80 transition-opacity ${isSidebarCollapsed ? 'justify-center w-full py-1.5' : 'px-1 py-1'}`}
          >
            <span className="material-symbols-outlined text-lg md:text-base" style={{ fontVariationSettings: "'FILL' 1" }}>add</span>
            {!isSidebarCollapsed && <span className="text-sm md:text-[13px]">Novo Chat</span>}
          </button>

          {/* Contexto do Chat (P23F): histórico ocupa o espaço flexível da
              metade superior da sidebar; navegação global + conta ficam
              fixas na metade inferior (ver abaixo), sem disputar espaço.
              min-h garante que o histórico nunca suma por completo mesmo
              sob forte pressão de altura — no mínimo cabe ~2 itens com
              scroll, em vez de 0px. */}
          {!isSidebarCollapsed && (
            <nav className="flex-1 overflow-y-auto mb-2 min-h-[88px]">
              <div className="mb-1.5 px-1 flex items-center justify-between gap-2 text-on-surface-variant/50 text-xs md:text-[11px] font-semibold uppercase tracking-wider">
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[14px] md:text-[12px]">history</span>
                  Histórico
                </span>
                {/* Busca no histórico (P23F) — só o ícone fica visível por
                    padrão (P23K); clicar revela um campo compacto. */}
                <button
                  type="button"
                  onClick={() => setShowHistorySearch(v => { const next = !v; if (!next) setHistorySearch(''); return next; })}
                  aria-label={showHistorySearch ? 'Fechar busca' : 'Buscar conversa'}
                  aria-expanded={showHistorySearch}
                  className="text-on-surface-variant/50 hover:text-primary transition-colors normal-case tracking-normal font-normal"
                >
                  <span className="material-symbols-outlined text-[14px] md:text-[13px]">{showHistorySearch ? 'close' : 'search'}</span>
                </button>
              </div>

              {showHistorySearch && (
                <input
                  type="text"
                  autoFocus
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Buscar conversa..."
                  aria-label="Buscar conversa no histórico"
                  className="w-full mb-1.5 px-2.5 py-1.5 text-sm md:text-[13px] rounded-lg bg-surface-container text-on-surface placeholder:text-on-surface-variant/40 border border-outline-variant/20 focus:ring-1 focus:ring-primary/50 outline-none"
                />
              )}

              <div className="flex flex-col space-y-0.5">
                {/* Current conversation marker */}
                {!historySearch.trim() && (
                  <div className="flex items-center gap-2 px-3 py-2 md:py-1.5 md:min-h-control-sm text-primary font-medium bg-primary/10 rounded-lg text-sm md:text-[13px] divine-glow-active">
                    <span className="material-symbols-outlined text-[16px] md:text-[14px]">chat</span>
                    <span className="truncate flex-1">Conversa atual</span>
                  </div>
                )}

                {conversationsError && (
                  <div className="px-1 py-1">
                    <ErrorRetryCard message={conversationsError} onRetry={fetchConversations} />
                  </div>
                )}

                {filteredConversations.map(conv => (
                  <div key={conv.id} className={`group flex items-center gap-1 rounded-lg transition-colors ${conv.id === conversationId ? 'bg-primary/5' : 'hover:bg-primary/5'}`}>
                    {renamingId === conv.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onBlur={() => submitRename(conv.id)}
                        onKeyDown={e => { if (e.key === 'Enter') submitRename(conv.id); if (e.key === 'Escape') setRenamingId(null); }}
                        className="flex-1 text-xs bg-surface-container px-2 py-2 md:py-1.5 rounded text-on-surface outline-none border border-primary/40 mx-1"
                      />
                    ) : (
                      <>
                        <button
                          className="flex-1 text-left px-3 py-2 md:py-1.5 md:min-h-control-sm text-sm md:text-[13px] text-on-surface/70 hover:text-on-surface truncate"
                          onClick={() => loadConversation(conv.id)}
                        >
                          {conv.title || 'Sem título'}
                        </button>
                        <div className="flex opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity pr-1 gap-0.5">
                          <button onClick={() => startRename(conv)} title="Renomear" className="p-1.5 md:p-1 text-on-surface-variant/50 hover:text-primary transition-colors">
                            <span className="material-symbols-outlined text-[14px] md:text-[13px]">edit</span>
                          </button>
                          <button onClick={() => deleteConversation(conv.id)} title="Excluir" className="p-1.5 md:p-1 text-on-surface-variant/50 hover:text-error transition-colors">
                            <span className="material-symbols-outlined text-[14px] md:text-[13px]">delete</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {conversations.length === 0 && (
                  <p className="px-3 py-2 text-xs text-on-surface-variant/40 italic">Nenhuma conversa ainda.</p>
                )}
                {conversations.length > 0 && filteredConversations.length === 0 && (
                  <p className="px-3 py-2 text-xs text-on-surface-variant/40 italic">Nenhuma conversa encontrada.</p>
                )}
              </div>
            </nav>
          )}

          {isSidebarCollapsed && <div className="flex-1" />}

          {/* Navegação global e conta (P23E/P23F): a partir do desktop
              intermediário este painel já é o único lugar onde a navegação
              global aparece, então fica aqui. No mobile puro (P23M), a
              barra inferior compartilhada com o resto do app (Início,
              Leitura, Chat, Trilhas, Caderno + "Mais") assume esse papel —
              repeti-la aqui também seria redundante com o que já aparece
              fixo no rodapé da tela. */}
          {screenTier !== 'mobile' && (
            <>
              <div className="pt-2 mt-1 border-t border-divider">
                <MainNav collapsed={isSidebarCollapsed} />
              </div>
              <div className="pt-2 mt-1 border-t border-divider">
                <AccountMenu collapsed={isSidebarCollapsed} onOpenSettings={() => { setIsSettingsOpen(true); closeSidebarOverlay(); }} />
              </div>
            </>
          )}

          {!isSidebarCollapsed && (
            <p className="font-serif italic text-[10px] text-on-surface-variant/30 text-center mt-2 px-2">Ad Maiorem Dei Gloriam</p>
          )}
        </div>
      </aside>

      {/* Overlay/backdrop do drawer — mobile e desktop intermediário (P23F) */}
      {isSidebarOverlay && (
        <div className="fixed inset-0 bg-black/60 z-40" onClick={closeSidebarOverlay} aria-hidden="true" />
      )}

      {/* Barra inferior mobile (P23M): mesmos 5 destinos + "Mais" usados em
          todas as outras páginas (AppShell), para que a navegação não mude
          de forma quando o usuário entra no Chat. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 pb-safe-bottom bg-surface-container-low border-t border-divider z-40">
        <div className="flex items-stretch justify-between w-full min-h-control">
          <MainNav items={MOBILE_PRIMARY_ITEMS} orientation="horizontal" />
          <button
            onClick={() => setIsMoreOpen(true)}
            aria-label="Mais"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 text-[11px] text-on-surface/70 hover:text-on-surface transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">more_horiz</span>
            Mais
          </button>
        </div>
      </div>

      <MobileMoreSheet
        open={isMoreOpen}
        onClose={() => setIsMoreOpen(false)}
        onOpenSettings={() => { setIsSettingsOpen(true); setIsMoreOpen(false); }}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative h-full pt-[calc(theme(spacing.mobile-header)+env(safe-area-inset-top))] md:pt-0 bg-surface overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20" style={{ background: 'radial-gradient(circle at 50% -20%, rgba(212,175,55,0.15) 0%, rgba(17,20,23,0) 60%)' }} />

        {/* Cabeçalho da conversa (P23F): título + persona + Sínodo, ao lado
            do título — não mais espalhados entre a sidebar e o input. */}
        <div className="relative z-10 flex items-center justify-between gap-3 px-4 md:px-10 py-3 border-b border-outline-variant/10 bg-surface-container-low/40 backdrop-blur-sm">
          <h2 className="text-sm font-medium text-on-surface truncate">{currentConversationTitle}</h2>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={selectedPersona}
              onChange={(e) => setSelectedPersona(e.target.value)}
              aria-label="Persona da conversa"
              title="Personas bloqueadas requerem upgrade de plano"
              className="min-h-control px-2 py-1.5 text-xs rounded-lg bg-surface-container text-on-surface border border-outline-variant/20 focus:ring-1 focus:ring-primary/50 outline-none"
            >
              {personas.map(p => {
                let allowed = true;
                const activePlan = planCatalog.find(plan => plan.plan_id === (currentPlan || 'trial')) || planCatalog[planCatalog.length - 1];
                if (activePlan?.entitlements?.chat_personas) {
                  allowed = activePlan.entitlements.chat_personas.includes(p.key);
                }
                return (
                  <option key={p.key} value={p.key} disabled={!allowed}>
                    {allowed ? p.label : `🔒 ${p.label}`}
                  </option>
                );
              })}
            </select>
            {currentPlan === 'premium' && (
              <button
                type="button"
                onClick={handleToggleSinodo}
                title={sinodoMode ? 'Desativar Sínodo Virtual' : `Ativar Sínodo Virtual (${synodCost ?? '?'} crédito${synodCost > 1 ? 's' : ''}): consulte o Teólogo e o Padre Amigo ao mesmo tempo`}
                className={`min-h-control flex items-center gap-1.5 px-2.5 rounded-full text-xs transition-colors ${sinodoMode ? 'bg-primary/15 text-primary' : 'text-on-surface-variant hover:bg-primary/5'}`}
              >
                <span>🕊️</span>
                <span className="hidden sm:inline">Sínodo</span>
                <span className={`relative w-8 h-4 rounded-full transition-colors ${sinodoMode ? 'bg-primary' : 'bg-surface-container-highest'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-on-primary transition-transform ${sinodoMode ? 'translate-x-4' : ''}`} />
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Chat flow */}
        <div className="flex-1 overflow-y-auto flex flex-col px-4 md:px-10 py-8 relative z-10 w-full max-w-content-reading mx-auto pb-44 md:pb-36">
          {messages.length === 0 ? (
            <div className="w-full flex justify-center mb-12 mt-8 md:mt-16">
              <div className="bg-white p-3 rounded-2xl shadow-[0px_16px_40px_rgba(0,0,0,0.12)] border border-outline-variant/20">
                <img alt="CatholicGPT Logo" className="w-16 h-16 object-contain rounded-xl" src="/logo-64.webp" />
              </div>
            </div>
          ) : (
            <MessageList
              messages={messages}
              loading={loading}
              onEditLastMessage={handleEditLastMessage}
              onDeleteLastMessage={handleDeleteLastMessage}
              onRegenerateLastMessage={loading ? undefined : handleRegenerateLastMessage}
              conversationId={conversationId}
            />
          )}
        </div>

        {/* Input area — deslocado para cima no mobile (P23M) para não ficar
            atrás da barra inferior fixa de navegação, que agora também
            aparece no Chat. */}
        {/* --keyboard-height é definida por useNativeShell no app nativo (0 na
            web e com o teclado fechado). Sem isso o teclado cobre o input. */}
        <div className="absolute bottom-[calc(4rem+env(safe-area-inset-bottom)+var(--keyboard-height,0px))] md:bottom-[var(--keyboard-height,0px)] left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-surface via-surface/90 to-transparent z-[45]">
          <ChatInput
            onSendMessage={handleSendMessage}
            onStop={handleStopGeneration}
            disabled={loading}
            sinodoMode={sinodoMode}
          />
        </div>

        {showSinodoDisclaimer && (
          <SinodoDisclaimerModal
            onAccept={handleAcceptSinodoDisclaimer}
            onCancel={() => setShowSinodoDisclaimer(false)}
          />
        )}
      </main>

      <SettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} availableCredits={availableCredits} />
    </div>
  );
};
